import React, { useCallback, useEffect, useMemo, useState } from "react";
import InputDropSearch from "../../../../components/inputs/InputDropSearch";
import {
  employeeProductRelease,
  searchReleaseEmployees,
} from "../../../../api/inventoryService";
import ReleaseProductModal from "../components/ReleaseProductModal";
import ReleaseLinesTable from "../components/ReleaseLinesTable";
import ReleasePayment from "../components/ReleasePayment";
import { linesTotal, ReleaseLine, staffName } from "../components/releaseTypes";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

/** Staff buys products and pays: Patient Release without prescriptions. */
function StaffRelease() {
  const [formData, setformData] = useState<FormData>({
    employeeId: "",
    discount: 0,
    card: 0,
    cash: 0,
    product: [],
  });
  const [employees, setEmployees] = useState<Employees[]>([]);
  const [open, setopen] = useState(false);

  const getEmployeesData = useCallback(async (search: string) => {
    try {
      const data = await searchReleaseEmployees(search);
      setEmployees(
        (data as Employees[]).map((e) => ({ ...e, fullName: staffName(e) }))
      );
    } catch (error) {
      console.error(error);
    }
  }, []);
  useEffect(() => {
    getEmployeesData("");
  }, [getEmployeesData]);

  const total = useMemo(() => linesTotal(formData.product), [formData.product]);
  const netTotal = total - (Number(formData.discount) || 0);

  const handleChange = useCallback((e: any) => {
    const { name, value } = e.target;
    setformData((prevData) => ({ ...prevData, [name]: value }));
  }, []);
  const addProduct = useCallback((product: ReleaseLine) => {
    setformData((prevFormData) => ({
      ...prevFormData,
      product: [...prevFormData.product, product],
    }));
    setopen(false);
  }, []);
  const removeProductById = useCallback((index: number) => {
    setformData((prevFormData) => ({
      ...prevFormData,
      product: prevFormData.product.filter((_product, i) => i !== index),
    }));
  }, []);
  const navigate = useNavigate();

  const handleSubmit = (e: any) => {
    e.preventDefault();

    if (!formData.employeeId) {
      toast.error("Choose a staff member first.");
      return;
    }
    if (formData.product.length === 0) {
      toast.error("Add a product first.");
      return;
    }

    employeeProductRelease({
      employeeId: formData.employeeId,
      discount: Number(formData.discount) || 0,
      card: Number(formData.card) || 0,
      cash: Number(formData.cash) || 0,
      total,
      netTotal,
      product: formData.product,
    })
      .then(() => {
        navigate("/client/inventory/release/all-release?type=employee");
      })
      .catch((error) => {
        console.error(error?.response);
        toast.error(
          error?.response?.data?.message || "Failed to complete payment."
        );
      });
  };
  return (
    <div className="bg-white dark:bg-slate-800 rounded-[8px] shadow-lg shadow-slate-200/50 dark:shadow-slate-900/50 border border-slate-100 dark:border-slate-700 overflow-hidden">
      {open && (
        <ReleaseProductModal
          title="Add Product to Staff"
          releaseType="employee"
          addProduct={addProduct}
          closeModal={() => setopen(false)}
        />
      )}

      <form onSubmit={handleSubmit} className="p-3 space-y-3">
        <InputDropSearch
          data={employees}
          name="employeeId"
          label="Staff"
          selectName1="fullName"
          selectValue="_id"
          onChange={handleChange}
          onSearch={getEmployeesData}
        />

        <button
          type="button"
          onClick={() => setopen(true)}
          className="w-full px-3 py-2 rounded-lg border-2 border-dashed border-primary/30 dark:border-primary/50 text-primary font-medium hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors flex items-center justify-center gap-1.5 text-xs"
        >
          <span className="text-base">+</span> Add Product
        </button>

        <ReleaseLinesTable lines={formData.product} onRemove={removeProductById} />

        <ReleasePayment
          total={total}
          netTotal={netTotal}
          discount={formData.discount}
          card={formData.card}
          cash={formData.cash}
          onChange={handleChange}
        />

        <button
          type="submit"
          className="w-full px-4 py-2.5 rounded-lg bg-gradient-to-r from-primary to-primaryLight text-white font-semibold hover:shadow-lg hover:shadow-primary/20 transition-all text-xs"
        >
          Complete Payment
        </button>
      </form>
    </div>
  );
}

export default StaffRelease;
interface Employees {
  _id: string;
  firstName: string;
  lastName?: string;
  fullName?: string;
}
interface FormData {
  employeeId: string;
  product: ReleaseLine[];
  discount: number;
  card: number;
  cash: number;
}
